import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useProfileUnlockStatus } from "@/hooks/useProfileUnlockStatus";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  X,
  Send,
  Smile,
  Paperclip,
  Phone,
  Video,
  Info,
  Crown,
  Minus,
  Image,
  Camera,
  MessageCircle,
  Mic,
  AlertTriangle,
  Maximize2,
  Minimize2,
  Lock,
} from "lucide-react";
import { useChatFontSize } from "@/hooks/useChatFontSize";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";
import { AudioMessage } from "@/components/ui/AudioMessage";
import { useToast } from "@/hooks/use-toast";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { protectedImageProps, protectedImageClass } from "@/lib/protected-image";
import { POLL_MESSAGES, POLL_PARTNER_STATUS } from "@/lib/polling";
import { OutOfCreditsModal } from "@/components/out-of-credits-modal";
import { OfflinePhotoHint } from "@/components/offline-photo-hint";
import { useVoiceCall } from "@/components/voice-call/VoiceCallProvider";
import { CallMessageBubble } from "@/components/voice-call/CallMessageBubble";
import { CALL_COPY } from "@/components/voice-call/call-copy";

interface Message {
  id: string;
  content: string;
  senderId: string;
  conversationId: string;
  createdAt: string;
  isRead: boolean;
}

interface ChatUser {
  id: string;
  firstName: string;
  profileImageUrl?: string;
  isPremium: boolean;
  isOnline: boolean;
}

interface ChatWindow {
  id: string;
  user: ChatUser;
  conversationId?: string | null;
  isMinimized: boolean;
}

interface MessengerChatWindowProps {
  chatWindow: ChatWindow;
  onClose: () => void;
  onMinimize: () => void;
  onExpand: () => void;
  style?: React.CSSProperties;
}

export default function MessengerChatWindow({
  chatWindow,
  onClose,
  onMinimize,
  onExpand,
  style,
}: MessengerChatWindowProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [newMessage, setNewMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  // Testo messaggi ingrandibile col pulsante "Aa" (condiviso con la chat mobile)
  const { bubbleTextClass, cycleFontSize, isDefaultSize } = useChatFontSize();
  // Modalita' a schermo intero per la finestra desktop
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Esc esce dalla modalita' espansa, come da convenzione
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsFullscreen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isFullscreen]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [icebreakerLoading, setIcebreakerLoading] = useState(false);
  const [icebreakerUsedThisSession, setIcebreakerUsedThisSession] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  const { user: recipientUserBase, isMinimized } = chatWindow;
  const [onlineStatus, setOnlineStatus] = useState(recipientUserBase.isOnline);
  // Profilo Premium non sbloccato: scrittura bloccata. L'acquisto si fa dal profilo.
  const { locked: profileLocked, cost: unlockCost } = useProfileUnlockStatus(recipientUserBase?.id);

  // isAiProfile is often NOT carried in the openChat payload (varies by call site),
  // so always fetch the recipient's details to resolve it — needed for the
  // private-photo button. Also polled every 3s so the online status stays fresh
  // via DB even when the WebSocket online_status_update broadcast is unavailable
  // (e.g. AI cron runs in a separate service and no longer pushes over WS).
  // Endpoint "status": UNA query invece delle cinque di /api/users/:id (che carica
  // anche foto pubbliche, private e sblocchi contenuti). A 3 secondi di polling era
  // l'endpoint piu' caro dell'app.
  const { data: recipientDetails } = useQuery<any>({
    queryKey: ["/api/users", recipientUserBase.id, "status"],
    queryFn: async () => {
      const res = await fetch(`/api/users/${recipientUserBase.id}/status`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!recipientUserBase.id,
    refetchInterval: POLL_PARTNER_STATUS,
  });

  // Keep the WS-driven onlineStatus in sync with the polled DB value as a fallback:
  // the WS push (when present) still updates it instantly via setOnlineStatus below.
  useEffect(() => {
    if (recipientDetails && typeof recipientDetails.isOnline === "boolean") {
      setOnlineStatus(recipientDetails.isOnline);
    }
  }, [recipientDetails?.isOnline]);

  const recipientUser = {
    ...recipientUserBase,
    isOnline: onlineStatus,
    isAiProfile: (recipientUserBase as any).isAiProfile || recipientDetails?.isAiProfile,
    hasPrivateGallery: (recipientUserBase as any).hasPrivateGallery ?? recipientDetails?.hasPrivateGallery,
    // La foto arriva da chi ha aperto la chat e non sempre e' valorizzata:
    // in quel caso mostravamo l'iniziale al posto dell'avatar. Ripieghiamo sul
    // dato completo gia' scaricato da /api/users/:id.
    profileImageUrl: recipientUserBase.profileImageUrl || recipientDetails?.profileImageUrl,
    canVoiceCall: !!recipientDetails?.canVoiceCall,
  };
  const voiceCall = useVoiceCall();

  // Close emoji picker on click outside
  useEffect(() => {
    if (!showEmojiPicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showEmojiPicker]);

  const handleEmojiSelect = useCallback((emoji: any) => {
    setNewMessage((prev) => prev + emoji.native);
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  }, []);

  // Auto-resize textarea on programmatic value updates (icebreaker, emoji picker).
  // When empty, reset to "" so the rows={1} default kicks in and we don't end up
  // with a 2-line tall input on first paint.
  useEffect(() => {
    const ta = inputRef.current;
    if (!ta) return;
    if (!newMessage) {
      ta.style.height = "";
      return;
    }
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, [newMessage]);

  // Listen for online status updates from WebSocket
  useEffect(() => {
    const handler = (e: Event) => {
      const statuses = (e as CustomEvent).detail as Record<string, boolean>;
      if (recipientUserBase.id in statuses) {
        setOnlineStatus(statuses[recipientUserBase.id]);
      }
    };
    window.addEventListener('online-status-update', handler);
    return () => window.removeEventListener('online-status-update', handler);
  }, [recipientUserBase.id]);
  const [currentConversationId, setCurrentConversationId] = useState<
    string | null
  >(chatWindow.conversationId ? String(chatWindow.conversationId) : null);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [isAudioSending, setIsAudioSending] = useState(false);
  const [isImageSending, setIsImageSending] = useState(false);
  const [photoLightbox, setPhotoLightbox] = useState<string | null>(null);
  // Popup crediti esauriti: sostituisce il toast quando la chat si blocca.
  const [showOutOfCredits, setShowOutOfCredits] = useState(false);
  // Popolato quando un'azione viene rifiutata per crediti insufficienti: fa
  // passare il popup dalla modalita' listino a quella "ti mancano N crediti".
  const [creditShortfall, setCreditShortfall] = useState<{
    cost: number;
    available: number;
    actionLabel?: string;
  } | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const { data: publicSettings } = useQuery<any[]>({
    queryKey: ["/api/settings/public"],
    queryFn: async () => { const r = await fetch("/api/settings/public"); return r.json(); },
    staleTime: 5 * 60 * 1000,
  });
  const chatImagesEnabled = publicSettings?.find((s: any) => s.key === "chat_images_enabled")?.value === "true";

  // Audio recording hook
  const { isRecording, duration, startRecording, stopRecording, cancelRecording } = useAudioRecorder();

  // Trova o crea una conversazione se non esiste
  const { data: conversation } = useQuery({
    queryKey: ["/api/conversations/find", recipientUser.id],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/conversations/find/${recipientUser.id}`,
        {
          credentials: "include",
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        }
      );
      if (!response.ok) return null;
      return response.json();
    },
    enabled: !currentConversationId && !!recipientUser.id,
    staleTime: 2 * 60 * 1000, // Cache per 2 minuti
  });

  // Aggiorna conversationId quando trova una conversazione esistente
  useEffect(() => {
    if (conversation && !currentConversationId) {
      setCurrentConversationId(
        conversation.id ? String(conversation.id) : null
      );
    }
  }, [conversation, currentConversationId]);

  useEffect(() => {
    const normalizeId = (value: any) =>
      value === undefined || value === null ? null : String(value);

    const getActiveConversationId = () => {
      const candidate =
        conversation?.id ?? currentConversationId ?? chatWindow.conversationId;
      return normalizeId(candidate);
    };

    const handleTyping = (event: Event) => {
      const detail = (event as CustomEvent<any>).detail;
      const conversationId = normalizeId(detail?.conversationId);
      if (!conversationId) return;
      if (conversationId === getActiveConversationId()) {
        setIsTyping(true);
        setShouldAutoScroll(true);
      }
    };

    const handleStopTyping = (event: Event) => {
      const detail = (event as CustomEvent<any>).detail;
      const conversationId = normalizeId(detail?.conversationId);
      if (!conversationId) return;
      if (conversationId === getActiveConversationId()) {
        setIsTyping(false);
      }
    };

    window.addEventListener("chat-typing", handleTyping as EventListener);
    window.addEventListener(
      "chat-stop-typing",
      handleStopTyping as EventListener
    );

    return () => {
      window.removeEventListener(
        "chat-typing",
        handleTyping as EventListener
      );
      window.removeEventListener(
        "chat-stop-typing",
        handleStopTyping as EventListener
      );
    };
  }, [conversation?.id, currentConversationId, chatWindow.conversationId]);

  // Fetch messages - parte quando la chat non è minimizzata
  const { data: messages = [], isLoading } = useQuery({
    queryKey: ["/api/conversations", currentConversationId, "messages"],
    queryFn: async () => {
      if (!currentConversationId) return [];
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/conversations/${currentConversationId}/messages`, {
        credentials: "include",
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!response.ok) {
        throw new Error("Failed to fetch messages");
      }
      return response.json();
    },
    enabled: !!currentConversationId,
    refetchInterval: POLL_MESSAGES,
  });

  // Rompisilenzio (ice-breaker) — only relevant before any message exists.
  type IcebreakerAvailability = { available: boolean; reason?: string; generationsUsed: number; cost: number; credits?: number };
  const { data: icebreakerAvailability, refetch: refetchIcebreakerAvailability } = useQuery<IcebreakerAvailability>({
    queryKey: ["/api/icebreaker/availability", recipientUser.id],
    queryFn: async () => {
      const r = await fetch(`/api/icebreaker/availability/${recipientUser.id}`, { credentials: "include" });
      if (!r.ok) throw new Error("availability failed");
      return r.json();
    },
    enabled: !!recipientUser.id && messages.length === 0,
    staleTime: 30 * 1000,
  });

  // Stessa ragione del guard su chatPhotoInFlight.
  const icebreakerInFlight = useRef(false);
  const handleIcebreaker = async () => {
    if (!recipientUser.id || icebreakerInFlight.current) return;
    icebreakerInFlight.current = true;
    setIcebreakerLoading(true);
    try {
      const r = await fetch("/api/icebreaker/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ targetUserId: recipientUser.id }),
      });
      const data = await r.json();
      if (!r.ok) {
        toast({
          title: "Rompisilenzio non disponibile",
          description: data?.error || "Riprova",
          variant: "destructive",
        });
        return;
      }
      setNewMessage(data.text);
      setIcebreakerUsedThisSession((n) => n + 1);
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      refetchIcebreakerAvailability();
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (err: any) {
      toast({ title: "Errore", description: err?.message, variant: "destructive" });
    } finally {
      icebreakerInFlight.current = false;
      setIcebreakerLoading(false);
    }
  };

  // "Richiedi foto": generata al volo dal contesto della conversazione.
  // `canRequest`/`retryAfterMs` riflettono i limiti di frequenza (attesa minima
  // + turno di conversazione) per QUESTA chat, quindi la query e' per profilo.
  const { data: chatPhotoAvailability } = useQuery<{
    enabled: boolean;
    cost: number;
    canRequest: boolean;
    reason?: "cooldown" | "needs_turn";
    retryAfterMs: number;
  }>({
    queryKey: ["/api/chat/photo/availability", recipientUser.id],
    queryFn: async () => {
      const qs = recipientUser.id ? `?profileId=${encodeURIComponent(recipientUser.id)}` : "";
      const r = await fetch(`/api/chat/photo/availability${qs}`, { credentials: "include" });
      if (!r.ok) throw new Error("availability");
      return r.json();
    },
    // Volutamente breve: dopo una foto il bottone deve tornare attivo da solo
    // allo scadere dell'attesa, senza che l'utente ricarichi la chat.
    staleTime: 10 * 1000,
    retry: false,
  });

  // Countdown dell'attesa fra due foto. Parte dal `retryAfterMs` del server (che
  // resta l'autorita') e scala in locale ogni secondo, cosi' il bottone si
  // riattiva da solo senza interrogare il server di continuo.
  const [chatPhotoRetryMs, setChatPhotoRetryMs] = useState(0);
  useEffect(() => {
    setChatPhotoRetryMs(chatPhotoAvailability?.retryAfterMs ?? 0);
  }, [chatPhotoAvailability?.retryAfterMs]);
  useEffect(() => {
    if (chatPhotoRetryMs <= 0) return;
    const t = setInterval(() => {
      setChatPhotoRetryMs((ms) => Math.max(0, ms - 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [chatPhotoRetryMs > 0]);

  // Costo foto privata: letto dal server, cosi' la label segue il setting.
  const { data: privatePhotoCost } = useQuery<{ cost: number }>({
    queryKey: ["/api/private-photo/cost"],
    queryFn: async () => {
      const r = await fetch("/api/private-photo/cost", { credentials: "include" });
      if (!r.ok) throw new Error("cost");
      return r.json();
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const [chatPhotoLoading, setChatPhotoLoading] = useState(false);
  // Il guard deve stare su una ref, non sullo state: setState e' asincrono, quindi
  // due tap ravvicinati leggono entrambi chatPhotoLoading === false e partono due
  // fetch (due foto generate, crediti scalati due volte). Su Android capita spesso
  // perche' il browser emette sia touch sia il click sintetico. La ref si aggiorna
  // subito, quindi la seconda chiamata esce prima di partire.
  const chatPhotoInFlight = useRef(false);
  const handleRequestChatPhoto = async () => {
    if (!recipientUser.id || chatPhotoInFlight.current) return;
    chatPhotoInFlight.current = true;
    setChatPhotoLoading(true);
    try {
      const r = await fetch("/api/chat/photo/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ profileId: recipientUser.id }),
      });
      const data = await r.json();
      if (!r.ok) {
        // Limite di frequenza: non e' un errore, e' il bottone che doveva gia'
        // essere spento. Niente toast rosso: riallineiamo lo stato al server
        // (che e' l'autorita') e il bottone si disabilita da solo.
        if (r.status === 429) {
          // Mai accorciare un'attesa gia' in corso: i limiti sono piu' d'uno
          // (antifrode a 20s e cooldown a minuti) e vince il piu' lungo.
          if (typeof data?.retryAfterMs === "number") {
            setChatPhotoRetryMs((ms) => Math.max(ms, data.retryAfterMs));
          }
          queryClient.invalidateQueries({
            queryKey: ["/api/chat/photo/availability", recipientUser.id],
          });
          return;
        }
        // Crediti insufficienti non e' un errore dell'utente: e' il momento in
        // cui vuole comprare. Un toast rosso lo informa e basta; il popup gli
        // dice quanto manca e glielo fa sbloccare in un tap.
        if (data?.cost != null) {
          setCreditShortfall({
            cost: data.cost,
            available: (user as any)?.credits ?? 0,
            actionLabel: "richiedere una foto",
          });
          setShowOutOfCredits(true);
          return;
        }
        toast({
          title: "Richiesta non riuscita",
          description: data?.error || "Riprova",
          variant: "destructive",
        });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      // Fa ripartire il countdown dell'attesa appena scattata.
      queryClient.invalidateQueries({
        queryKey: ["/api/chat/photo/availability", recipientUser.id],
      });
      if (currentConversationId) {
        queryClient.invalidateQueries({ queryKey: ["/api/conversations", currentConversationId, "messages"] });
      }
    } catch (err: any) {
      toast({ title: "Errore", description: err?.message, variant: "destructive" });
    } finally {
      chatPhotoInFlight.current = false;
      setChatPhotoLoading(false);
    }
  };

  const [privatePhotoLoading, setPrivatePhotoLoading] = useState(false);
  // "Richiedi foto privata" e' secondario: sta chiuso dietro l'icona immagine
  // nella riga di input, per non occupare due fasce intere sopra l'input.
  const [showPrivatePhotoAction, setShowPrivatePhotoAction] = useState(false);
  // Stessa ragione del guard su chatPhotoInFlight: anche qui si scalano crediti.
  const privatePhotoInFlight = useRef(false);
  const handleRequestPrivatePhoto = async () => {
    if (!recipientUser.id || privatePhotoInFlight.current) return;
    privatePhotoInFlight.current = true;
    setPrivatePhotoLoading(true);
    try {
      const r = await fetch("/api/chat/request-private-photo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ profileId: recipientUser.id }),
      });
      const data = await r.json();
      if (!r.ok) {
        toast({
          title: "Richiesta non riuscita",
          description: data?.cost
            ? `Servono ${data.cost} crediti per richiedere una foto privata`
            : data?.error || "Riprova",
          variant: "destructive",
        });
        return;
      }
      // delivered:false (declined / pool_empty) non è un errore: l'AI ha già risposto in chat.
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      if (currentConversationId) {
        queryClient.invalidateQueries({ queryKey: ["/api/conversations", currentConversationId, "messages"] });
      }
    } catch (err: any) {
      toast({ title: "Errore", description: err?.message, variant: "destructive" });
    } finally {
      privatePhotoInFlight.current = false;
      setPrivatePhotoLoading(false);
    }
  };

  // Condizioni dei due pulsanti foto: servono sia al blocco sopra l'input sia al
  // toggle nella riga di input, quindi stanno qui e non dentro il render.
  // Il bottone non dipende piu' da quanti messaggi ha la chat: resta disponibile
  // dal primo messaggio in poi (scelta di prodotto, 2026-09-20). Restano fuori
  // solo i casi in cui non avrebbe senso: profilo non AI, feature spenta da admin.
  //
  // L'offline NON nasconde piu' i pulsanti (scelta di prodotto, 2026-09-21):
  // restano visibili e spenti, col motivo in un suggerimento a richiesta (hover
  // o tap) — vedi OfflinePhotoHint. `show*` decide se il pulsante esiste,
  // `recipientOffline` se e' cliccabile.
  const recipientOffline =
    (recipientUser as any)?.isAiProfile && !recipientUser.isOnline;
  const showChatPhoto =
    messages.length > 0 &&
    (recipientUser as any)?.isAiProfile &&
    chatPhotoAvailability?.enabled;
  const hasPrivatePhoto =
    messages.length > 0 &&
    (recipientUser as any)?.isAiProfile &&
    (recipientUser as any)?.hasPrivateGallery;

  // Calcola i messaggi non letti per questa conversazione (solo messaggi dell'altro utente non ancora letti)
  const unreadMessagesCount = messages.filter(
    (message: any) => message.senderId === recipientUser.id && !message.isRead
  ).length;

  // Traccia i nuovi messaggi per questa conversazione quando è minimizzata
  // Also hide typing indicator when AI response arrives
  const [lastReceivedMessageCount, setLastReceivedMessageCount] = useState(0);

  useEffect(() => {
    if (messages) {
      // Conta solo i messaggi ricevuti (non inviati da me)
      const receivedMessages = messages.filter(
        (msg: any) => msg.senderId !== (user as any)?.id
      );

      // Hide typing indicator when a new AI message arrives
      if (receivedMessages.length > lastReceivedMessageCount && isTyping) {
        setIsTyping(false);
      }
      setLastReceivedMessageCount(receivedMessages.length);

      if (isMinimized) {
        if (receivedMessages.length > lastMessageCount && lastMessageCount > 0) {
          setHasNewMessage(true);
        }
        setLastMessageCount(receivedMessages.length);
      } else {
        // Quando la chat viene espansa, resetta l'indicatore
        setHasNewMessage(false);
      }
    }
  }, [messages, isMinimized, lastMessageCount, lastReceivedMessageCount, isTyping, (user as any)?.id]);

  // Send message mutation with optimistic UI
  const sendMessageMutation = useMutation({
    mutationFn: async (content: string) => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch("/api/messages/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: "include",
        body: JSON.stringify({
          recipientId: recipientUser.id,
          content,
          conversationId: currentConversationId || null,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to send message");
      }
      return response.json();
    },
    onMutate: async (content: string) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["/api/conversations", currentConversationId, "messages"],
      });

      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData([
        "/api/conversations",
        currentConversationId,
        "messages",
      ]);

      // Optimistically update to the new value
      if (currentConversationId) {
        queryClient.setQueryData(
          ["/api/conversations", currentConversationId, "messages"],
          (old: any[]) => {
            const optimisticMessage = {
              id: `temp-${Date.now()}`,
              content,
              senderId: (user as any)?.id,
              recipientId: recipientUser.id,
              conversationId: currentConversationId,
              createdAt: new Date().toISOString(),
              isRead: false,
              _isOptimistic: true,
            };
            return [...(old || []), optimisticMessage];
          }
        );
      }

      // Return context object with the snapshotted value
      return { previousMessages, originalContent: content };
    },
    onError: (err: any, variables, context) => {
      // Rollback on error
      if (context?.previousMessages && currentConversationId) {
        queryClient.setQueryData(
          ["/api/conversations", currentConversationId, "messages"],
          context.previousMessages
        );
      }
      console.error("Error sending message:", err);
      const msg = err?.message || "Errore nell'invio del messaggio";
      if (msg.includes("Crediti")) {
        setShowOutOfCredits(true);
      } else {
        toast({ title: "Errore", description: msg, variant: "destructive" });
      }
    },
    onSuccess: (data) => {
      const normalizedFromResponse = data.conversationId
        ? String(data.conversationId)
        : null;
      // Se non avevamo un conversationId, aggiornalo dal risultato
      if (!currentConversationId && normalizedFromResponse) {
        setCurrentConversationId(normalizedFromResponse);
      }

      // Typing indicator will be hidden when new AI messages arrive via refetch
      // This is handled by the message count check in useEffect

      // Refresh to get the real message from server
      queryClient.invalidateQueries({
        queryKey: [
          "/api/conversations",
          currentConversationId || normalizedFromResponse,
          "messages",
        ],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/conversations"] });
      // Il messaggio appena inviato e' il "turno" che sblocca la richiesta foto.
      queryClient.invalidateQueries({
        queryKey: ["/api/chat/photo/availability", recipientUser.id],
      });
      // Immediately update user credits in cache from response
      if (typeof data.currentCredits === 'number') {
        queryClient.setQueryData(["/api/user"], (oldUser: any) => {
          if (!oldUser) return oldUser;
          return { ...oldUser, credits: data.currentCredits };
        });
      }
      // Also invalidate for full refresh as fallback
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });

    },
  });

  // Auto-scroll to bottom ONLY when user sends a message or typing indicator shows
  // Initial positioning to show recent messages without animation
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);
  const [hasInitiallyScrolled, setHasInitiallyScrolled] = useState(false);

  // --- Cronologia: caricamento a ritroso (come nella pagina /chat) ---
  // Il server restituisce la finestra recente (ultimi 50). I precedenti si
  // chiedono con ?before=<createdAt del più vecchio che ho> e stanno in uno stato
  // separato, così il polling ogni 8s continua a rinfrescare solo la coda.
  const [olderMessages, setOlderMessages] = useState<any[]>([]);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Annulla il caricamento in volo al cambio conversazione: senza, la risposta
  // della chat precedente arrivava comunque e i suoi messaggi finivano in cima a
  // quella nuova. Il ref sull'id serve a leggere il valore CORRENTE dentro una
  // closure già creata (quello catturato resta fermo a quando è partita).
  const olderAbortRef = useRef<AbortController | null>(null);
  const currentConvIdRef = useRef<string | null>(null);

  // Cambio conversazione: la cronologia caricata non c'entra più nulla.
  useEffect(() => {
    currentConvIdRef.current = currentConversationId ?? null;
    setOlderMessages([]);
    setHasMoreHistory(true);
    setIsLoadingOlder(false);
    return () => {
      olderAbortRef.current?.abort();
      olderAbortRef.current = null;
    };
  }, [currentConversationId]);

  // Lista mostrata: cronologia caricata + finestra recente. Il filtro per id
  // protegge dai duplicati quando il polling ripesca l'ultimo della pagina.
  const allMessages = useMemo(() => {
    if (olderMessages.length === 0) return messages;
    const idRecenti = new Set((messages as any[]).map((m: any) => m.id));
    return [...olderMessages.filter((m: any) => !idRecenti.has(m.id)), ...(messages as any[])];
  }, [olderMessages, messages]);

  // Carica la pagina precedente mantenendo il punto di lettura: si misura
  // l'altezza del contenuto prima e dopo e si sposta lo scroll della differenza.
  const loadOlderMessages = useCallback(async () => {
    if (isLoadingOlder || !hasMoreHistory || !currentConversationId) return;
    const primo = olderMessages[0] ?? (messages as any[])[0];
    if (!primo?.createdAt) return;

    setIsLoadingOlder(true);
    const container = scrollContainerRef.current;
    const altezzaPrima = container?.scrollHeight ?? 0;
    const convDiPartenza = currentConversationId;
    const abort = new AbortController();
    olderAbortRef.current?.abort();
    olderAbortRef.current = abort;

    try {
      const token = localStorage.getItem("auth_token");
      const before = encodeURIComponent(new Date(primo.createdAt).toISOString());
      const res = await fetch(
        `/api/conversations/${convDiPartenza}/messages?limit=50&before=${before}`,
        {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: abort.signal,
        }
      );
      if (!res.ok) throw new Error("richiesta fallita");
      const pagina: any[] = await res.json();

      // Risposta di una chat che l'utente ha lasciato: va scartata.
      if (convDiPartenza !== currentConvIdRef.current) return;

      if (pagina.length === 0) {
        setHasMoreHistory(false);
      } else {
        if (pagina.length < 50) setHasMoreHistory(false);
        setOlderMessages((prev) => [...pagina, ...prev]);
        requestAnimationFrame(() => {
          const c = scrollContainerRef.current;
          if (c) c.scrollTop += c.scrollHeight - altezzaPrima;
        });
      }
    } catch (err) {
      // Un abort è il cambio conversazione, non un errore: silenzioso.
      if ((err as any)?.name !== "AbortError") {
        console.error("[MessengerChat] caricamento cronologia fallito:", err);
      }
    } finally {
      if (convDiPartenza === currentConvIdRef.current) {
        setIsLoadingOlder(false);
      }
    }
  }, [isLoadingOlder, hasMoreHistory, currentConversationId, olderMessages, messages]);

  const handleScroll = useCallback(() => {
    const c = scrollContainerRef.current;
    if (!c || c.scrollTop > 120) return;
    loadOlderMessages();
  }, [loadOlderMessages]);

  // Scroll to bottom when chat opens or when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && !isMinimized) {
      if (shouldAutoScroll) {
        // Smooth scroll when user sends message
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        setShouldAutoScroll(false);
      } else if (!hasInitiallyScrolled && messages.length > 0 && !isLoading) {
        // Instant scroll to bottom when chat opens (no animation)
        // Wait a bit to ensure DOM is ready
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "instant" });
            setHasInitiallyScrolled(true);
          }
        }, 100);
      }
    }
  }, [
    messages,
    isMinimized,
    isTyping,
    shouldAutoScroll,
    hasInitiallyScrolled,
    isLoading,
  ]);

  // Reset initial scroll flag when conversation changes or chat is expanded
  useEffect(() => {
    setHasInitiallyScrolled(false);
  }, [currentConversationId]);

  // Force scroll when chat is expanded from minimized state
  useEffect(() => {
    if (!isMinimized && messages.length > 0) {
      // Small delay to ensure the height transition completes
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "instant" });
        }
      }, 150);

    }
  }, [
    isMinimized,
    messages.length,
    recipientUser.id,
    currentConversationId,
    queryClient,
  ]);

  // Marca come letti i messaggi dell'altro utente mentre la chat e' aperta.
  //
  // Separato dall'effetto di scroll qui sopra, dove viveva prima: le dipendenze
  // di quello (isMinimized + messages.length) restano ferme una volta aperta la
  // chat, e messages.length veniva comunque consumato dall'inserimento
  // ottimistico dei messaggi inviati. Risultato: i messaggi in arrivo dopo il
  // primo turno non venivano piu' marcati. Vedi la nota estesa in chat.tsx.
  //
  // La chiave qui sotto dipende solo dai messaggi ricevuti non letti, quindi
  // cambia a ogni nuovo arrivo e mai per un messaggio proprio.
  const unreadIncomingKey = useMemo(() => {
    const unread = messages.filter(
      (message: any) =>
        message.senderId === recipientUser.id && !message.isRead,
    );
    if (unread.length === 0) return null;
    return `${unread.length}-${unread[unread.length - 1].id}`;
  }, [messages, recipientUser.id]);

  const lastMarkReadRef = useRef<string | null>(null);
  useEffect(() => {
    // Minimizzata la chat non e' letta: nessun messaggio e' davvero sotto gli occhi.
    if (isMinimized || !currentConversationId || !unreadIncomingKey) return;
    const markKey = `${currentConversationId}-${unreadIncomingKey}`;
    if (lastMarkReadRef.current === markKey) return;
    lastMarkReadRef.current = markKey;

    const token = localStorage.getItem('auth_token');
    fetch(`/api/conversations/${currentConversationId}/mark-read`, {
      method: "POST",
      credentials: "include",
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    }).then(() => {
      // Aggiorna i messaggi per riflettere lo stato letto
      queryClient.invalidateQueries({
        queryKey: ["/api/conversations", currentConversationId, "messages"],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/conversations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/messages/unread"] });
      queryClient.invalidateQueries({ queryKey: ["/api/messages/unread-count"] });
    });
  }, [isMinimized, currentConversationId, unreadIncomingKey, queryClient]);

  // Entrando o uscendo da schermo intero il contenitore cambia dimensione e il
  // contenuto si ridispone, ma la posizione di scroll resta quella di prima:
  // senza questo si finiva a meta' conversazione invece che sull'ultimo
  // messaggio. Gli altri effetti di scroll non bastano perche' dipendono da
  // messages/isMinimized, che qui non cambiano.
  useEffect(() => {
    if (isMinimized || !messages.length) return;
    // Due passate: la prima subito (a schermo intero la finestra si rimonta in
    // un portal, quindi il primo layout e' gia' quello nuovo), la seconda dopo
    // la transizione CSS del contenitore (duration-300), quando l'altezza
    // definitiva e' nota. Con la sola prima lo scroll finirebbe corto.
    const raf = requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
    });
    const t = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
    }, 320);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [isFullscreen]);

  // Focus input when chat is expanded
  useEffect(() => {
    if (!isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isMinimized]);

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || isImageSending) return;
    e.target.value = ""; // reset input

    setIsImageSending(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('user_id', (user as any)?.id || 'unknown');
      formData.append('profile_id', recipientUser.id);

      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/agent/image-chat', {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        if (typeof data.currentCredits === 'number') {
          queryClient.setQueryData(["/api/user"], (oldUser: any) => {
            if (!oldUser) return oldUser;
            return { ...oldUser, credits: data.currentCredits };
          });
        }
        queryClient.invalidateQueries({
          queryKey: ["/api/conversations", currentConversationId, "messages"],
        });
        queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      } else if (response.status === 403) {
        // Image blocked by moderation
        console.warn("[image-chat] Image blocked by moderation");
      }
    } catch (err) {
      console.error('Failed to send image:', err);
    } finally {
      setIsImageSending(false);
    }
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (newMessage.trim()) {
      const messageContent = newMessage.trim();

      // Pulisci immediatamente il campo input
      setNewMessage("");

      sendMessageMutation.mutate(messageContent);

      // Typing indicator is driven by WebSocket "chat-typing" event from server
      // (arrives after realistic delay: time-of-day + reading phase)
    }
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString("it-IT", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateSeparator = (timestamp: string | Date) => {
    const d = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const sameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
    if (sameDay(d, today)) return "Oggi";
    if (sameDay(d, yesterday)) return "Ieri";
    if (d.getFullYear() === today.getFullYear()) {
      return d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
    }
    return d.toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" });
  };

  const isSameDay = (a: string | Date, b: string | Date) => {
    const da = new Date(a);
    const db = new Date(b);
    return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
  };

  // A schermo intero la finestra viene montata in un portal su <body>: dentro
  // MultiChatContainer (fixed + pointer-events-none) un figlio `fixed` non si
  // comporta come previsto e resterebbe legato al layout del contenitore.
  const expanded = isFullscreen && !isMinimized;

  const windowEl = (
    <div
      className={`bg-white border transition-all duration-300 ${expanded
        ? "fixed inset-0 m-auto h-[90vh] w-[min(92vw,1100px)] max-w-none rounded-xl shadow-2xl border-gray-200"
        : isMinimized
          ? "w-[340px] lg:w-[360px] max-w-[95vw] h-12 shadow-lg border-gray-400 rounded-t-xl"
          : "w-[340px] lg:w-[360px] max-w-[95vw] h-[380px] lg:h-[520px] shadow-2xl border-gray-100 rounded-t-xl"
        }`}
      style={{
        // A schermo intero la posizione la decidono le classi: lo style inline
        // (right calcolato dal container) va ignorato, sposterebbe la finestra.
        ...(expanded ? {} : style),
        display: "flex",
        flexDirection: "column",
        alignSelf: "flex-end",
        ...(expanded ? { zIndex: 10001 } : null),
      }}
    >
      {/* Chat Header - Facebook Messenger Style */}
      <div
        className={`p-2 rounded-t-xl flex items-center justify-between transition-all duration-200 ${isMinimized
          ? "bg-white shadow-md border-b-0 h-12 cursor-pointer hover:bg-gray-50"
          : "bg-white border-gray-200 border-b h-auto"
          }`}
        onClick={() => {
          if (isMinimized) {
            onExpand();
          }
        }}
      >
        <div className="flex items-center space-x-3">
          <div
            className="relative cursor-pointer hover:opacity-80 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              setLocation(`/profile/${recipientUser.id}`);
            }}
            data-testid={`chat-avatar-${recipientUser.id}`}
          >
            <Avatar className="w-8 h-8 cursor-pointer">
              <AvatarImage
                src={recipientUser.profileImageUrl}
                alt={recipientUser.firstName}
                className="object-cover"
              />
              <AvatarFallback className="bg-blue-500 text-white cursor-pointer hover:bg-blue-600 transition-colors">
                {(recipientUser.firstName || 'U').charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            {recipientUser.isOnline && (
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white" />
            )}
            {/* Badge messaggi non letti */}
            {isMinimized && unreadMessagesCount > 0 && (
              <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-medium shadow-lg border-2 border-white z-10">
                {unreadMessagesCount > 99 ? "99+" : unreadMessagesCount}
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center space-x-1">
              <h3
                className="font-semibold text-sm text-gray-900 hover:text-blue-600 hover:underline cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setLocation(`/profile/${recipientUser.id}`);
                }}
                data-testid={`chat-username-${recipientUser.id}`}
              >
                {recipientUser.firstName}
              </h3>
              {recipientUser.isPremium && (
                <Crown className="w-3 h-3 text-yellow-400" />
              )}
            </div>
            <p className="text-xs text-gray-500">
              {recipientUser.isOnline ? "Attivo ora" : "Non in linea"}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          {/* Pulsanti chiamata/videochiamata nascosti
          <Button
            variant="ghost"
            size="sm"
            className="w-7 h-7 p-0 rounded-full opacity-40 cursor-not-allowed"
            disabled
          >
            <Phone className="w-3 h-3 text-gray-400" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-7 h-7 p-0 rounded-full opacity-40 cursor-not-allowed"
            disabled
          >
            <Video className="w-3 h-3 text-gray-400" />
          </Button>
          */}
          {!isMinimized && (
            <Button
              variant="ghost"
              size="sm"
              className={`w-7 h-7 p-0 hover:bg-gray-100 rounded-full font-semibold leading-none ${isDefaultSize ? "text-gray-600 hover:text-gray-800" : "text-blue-600 hover:text-blue-700"
                }`}
              onClick={(e) => {
                e.stopPropagation();
                cycleFontSize();
              }}
              title="Dimensione testo"
              aria-label="Cambia dimensione del testo"
              data-testid="button-chat-font-size"
            >
              <span className="text-[11px]">Aa</span>
            </Button>
          )}
          {!isMinimized && recipientUser.canVoiceCall && (
            <Button
              variant="ghost"
              size="sm"
              className="w-7 h-7 p-0 hover:bg-gray-100 rounded-full text-gray-600 hover:text-gray-800"
              disabled={voiceCall.phase !== "idle"}
              onClick={(e) => {
                e.stopPropagation();
                voiceCall.startCall({
                  id: recipientUser.id,
                  name: recipientUser.firstName || "",
                  photoUrl: recipientUser.profileImageUrl,
                });
              }}
              title={CALL_COPY.callButton}
              aria-label={CALL_COPY.callButton}
              data-testid="button-chat-voice-call"
            >
              <Phone className="w-3 h-3" />
            </Button>
          )}
          {!isMinimized && (
            <Button
              variant="ghost"
              size="sm"
              className="w-7 h-7 p-0 hover:bg-gray-100 rounded-full text-gray-600 hover:text-gray-800"
              onClick={(e) => {
                e.stopPropagation();
                setIsFullscreen((v) => !v);
              }}
              title={isFullscreen ? "Riduci" : "Espandi a schermo"}
              aria-label={isFullscreen ? "Riduci la finestra" : "Espandi a schermo intero"}
              data-testid="button-chat-fullscreen"
            >
              {isFullscreen ? (
                <Minimize2 className="w-3 h-3" />
              ) : (
                <Maximize2 className="w-3 h-3" />
              )}
            </Button>
          )}
          {!isMinimized && (
            <Button
              variant="ghost"
              size="sm"
              className="w-7 h-7 p-0 hover:bg-gray-100 rounded-full text-gray-600 hover:text-gray-800"
              onClick={(e) => {
                e.stopPropagation();
                onMinimize();
              }}
            >
              <Minus className="w-3 h-3" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-7 h-7 p-0 hover:bg-gray-100 rounded-full text-gray-600 hover:text-gray-800"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Chat Content - only show when not minimized */}
      {!isMinimized && (
        <>
          {/* Disclaimer AI: fuori dall'area scrollabile, resta sempre visibile */}
          {(recipientUser as any)?.isAiProfile && (
            <div className="shrink-0 bg-gray-50 px-3 py-1.5 text-center text-[11px] leading-relaxed text-gray-500" data-testid="text-ai-disclaimer">
              Amante AI • Non è una persona reale
            </div>
          )}
          {/* Messages Area - Facebook Messenger Style */}
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto p-3 bg-gray-50 flex flex-col"
            style={{ height: "420px" }}
          >
            {/* Cronologia precedente: si carica risalendo verso l'alto */}
            {isLoadingOlder && (
              <div className="flex-shrink-0 flex justify-center py-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-pink-500 border-t-transparent" />
              </div>
            )}
            {!hasMoreHistory && allMessages.length > 50 && (
              <p className="flex-shrink-0 py-2 text-center text-[11px] text-gray-400">
                Inizio della conversazione
              </p>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-sm text-gray-500">
                  Caricamento messaggi...
                </div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-[52px] h-[52px] bg-blue-100 rounded-full flex items-center justify-center mb-3">
                  <Avatar className="w-12 h-12">
                    <AvatarImage
                      src={recipientUser.profileImageUrl}
                      alt={recipientUser.firstName}
                      className="object-cover"
                    />
                    <AvatarFallback className="bg-blue-500 text-white">
                      {(recipientUser.firstName || 'U').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <h4 className="font-medium text-gray-900 mb-1">
                  {recipientUser.firstName}
                </h4>
                <p className="text-sm text-gray-500 mb-3">
                  Inizia una conversazione con {recipientUser.firstName}
                </p>
              </div>
            ) : (
              <>
                {/* Spacer per spingere i messaggi verso il basso naturalmente */}
                <div className="flex-1"></div>
                <div className="space-y-3">
                  {allMessages.map((message: Message, index: number) => {
                    const isMe = message.senderId === (user as any)?.id;
                    const prev = index > 0 ? allMessages[index - 1] : null;
                    const showDateSeparator =
                      !prev || !isSameDay(prev.createdAt, message.createdAt);
                    // `prev` (non messages[index-1]): l'indice si riferisce ad
                    // allMessages, che include la cronologia caricata a ritroso.
                    const showTime =
                      index === 0 ||
                      !prev ||
                      new Date(message.createdAt).getTime() -
                      new Date(prev.createdAt).getTime() >
                      5 * 60 * 1000;

                    return (
                      <div key={message.id}>
                        {showDateSeparator && (
                          <div className="flex justify-center my-2">
                            <span className="text-[11px] font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                              {formatDateSeparator(message.createdAt)}
                            </span>
                          </div>
                        )}
                        {showTime && !showDateSeparator && (
                          <div className="text-center mb-2">
                            <span className="text-xs text-gray-400 bg-white px-2 py-1 rounded-lg">
                              {formatTime(message.createdAt)}
                            </span>
                          </div>
                        )}

                        <div
                          className={`flex items-end gap-2 ${isMe ? "justify-end" : "justify-start"
                            }`}
                        >
                          {!isMe && (
                            <Avatar className="w-6 h-6 mb-1">
                              <AvatarImage
                                src={recipientUser.profileImageUrl}
                                alt={recipientUser.firstName}
                                className="object-cover"
                              />
                              <AvatarFallback className="text-xs bg-blue-500 text-white">
                                {(recipientUser.firstName || 'U').charAt(0).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                          )}

                          {(() => {
                            const content = message.content || "";
                            const isBlobUrl = content.startsWith("blob:");
                            const linkMatch = content.match(/https?:\/\/\S+/);
                            const extractedUrl = linkMatch ? linkMatch[0].replace(/[).,!?>\\"]+$/, "") : null;

                            // Chiamata vocale: bolla con durata (il transcript serve alla memoria AI).
                            if ((message as any).messageType === "call") {
                              return <CallMessageBubble metadata={(message as any).metadata} />;
                            }

                            const isAudioUrl = !!(isBlobUrl && !((message as any).messageType === "image")) || !!(extractedUrl && (/audio-messages\//.test(extractedUrl) || /\.(webm|mp3|wav|ogg|m4a)(\?\S*)?$/i.test(extractedUrl)));
                            const isImageUrl = (message as any).messageType === "image" || (isBlobUrl && (message as any).metadata?._isOptimistic) || !!(extractedUrl && (/\.(png|jpe?g|gif|webp)(\?\S*)?$/i.test(extractedUrl) || extractedUrl.startsWith("data:image") || /chat-images\//.test(extractedUrl)));

                            if (isImageUrl && !isAudioUrl) {
                              const meta = (message as any).metadata || {};
                              const isBlocked = meta.blocked === true || content === "[photo-blocked]";
                              const isPlaceholder = content === "[photo]" || content === "[photo-blocked]";

                              if (isBlocked) {
                                return (
                                  <div className="max-w-[240px] px-3 py-2 rounded-2xl border border-red-300 bg-red-50/60 text-red-700 dark:bg-red-950/30 dark:border-red-800 dark:text-red-200 text-sm">
                                    <div className="flex items-center gap-2 font-medium">
                                      <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
                                      <span>Immagine non consentita</span>
                                    </div>
                                  </div>
                                );
                              }

                              if (isPlaceholder) {
                                return (
                                  <div className={`max-w-[200px] px-3 py-2 rounded-2xl text-sm border ${isMe
                                    ? "bg-blue-600/40 text-blue-50 border-blue-400/60 rounded-br-md"
                                    : "bg-gray-50 text-gray-600 border-gray-300 rounded-bl-md dark:bg-gray-800/60 dark:text-gray-300 dark:border-gray-600"
                                  }`}>
                                    <div className="flex items-center gap-2 italic">
                                      <Image className="w-4 h-4" aria-hidden="true" />
                                      <span>Foto inviata</span>
                                    </div>
                                  </div>
                                );
                              }

                              // Legacy: real image with URL
                              const imgSrc = isBlobUrl ? content : (extractedUrl || content);
                              return (
                                <div className="max-w-[200px]">
                                  <img
                                    src={imgSrc}
                                    alt="Foto"
                                    className={`rounded-2xl max-w-[200px] max-h-[260px] object-cover cursor-pointer ${protectedImageClass}`}
                                    onClick={() => setPhotoLightbox(imgSrc)}
                                    {...protectedImageProps}
                                  />
                                </div>
                              );
                            }

                            const audioSource = isBlobUrl ? content : extractedUrl;
                            return (
                              <div
                                className={`px-3 py-2 rounded-2xl ${bubbleTextClass} ${isFullscreen ? "max-w-[min(70%,640px)]" : "max-w-[240px]"
                                  } ${isMe
                                    ? "bg-blue-600 text-white rounded-br-md"
                                    : "bg-white text-gray-900 border border-gray-200 rounded-bl-md shadow-sm"
                                  }`}
                              >
                                {isAudioUrl && audioSource ? (
                                  <AudioMessage
                                    audioUrl={audioSource}
                                    isOwn={isMe}
                                    duration={(message as any).metadata?.duration}
                                  />
                                ) : (
                                  <p className="whitespace-pre-wrap break-words">{content}</p>
                                )}
                                <p className={`text-[10px] leading-none mt-0.5 opacity-70 text-right ${isMe ? "text-blue-100" : "text-gray-500 dark:text-gray-400"}`}>
                                  {formatTime(message.createdAt)}
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })}

                  {/* Typing indicator */}
                  {isTyping && (
                    <div className="flex items-end gap-2 justify-start">
                      <Avatar className="w-6 h-6 mb-1">
                        <AvatarImage
                          src={recipientUser.profileImageUrl}
                          alt={recipientUser.firstName}
                          className="object-cover"
                        />
                        <AvatarFallback className="text-xs bg-blue-500 text-white">
                          {(recipientUser.firstName || 'U').charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>

                      <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md shadow-sm px-4 py-3">
                        <div className="flex items-center space-x-1">
                          <div className="flex space-x-1">
                            <div
                              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                              style={{ animationDelay: "0ms" }}
                            ></div>
                            <div
                              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                              style={{ animationDelay: "150ms" }}
                            ></div>
                            <div
                              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                              style={{ animationDelay: "300ms" }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              </>
            )}
          </div>

          {/* Input Area - Facebook Messenger Style */}
          <div className="p-3 bg-white border-t border-gray-100 rounded-b-xl">
            {profileLocked ? (
              /* Profilo Premium non sbloccato: stessa card della chat mobile.
                 Prima il blocco era comunicato solo dal placeholder di un campo
                 disabilitato, che veniva troncato ("Sblocca il profilo (100 cre...")
                 e lasciava visibili azioni non utilizzabili. */
              <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-4 py-4 text-center">
                <Lock
                  className="w-6 h-6 mx-auto mb-2 text-pink-500"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  Questo è un profilo Premium.
                  <br />
                  Sbloccalo dal profilo per chattare.
                </p>
                <button
                  type="button"
                  onClick={() => setLocation(`/profile/${recipientUser.id}`)}
                  className="text-sm font-medium rounded-full px-5 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700"
                >
                  Vai al profilo
                </button>
              </div>
            ) : (
            <>
            {messages.length === 0 && icebreakerAvailability && (
              (() => {
                const a = icebreakerAvailability;
                const cost = a.cost ?? 3;
                const isVisible = a.available || a.reason === "insufficient_credits" || a.reason === "limit_reached";
                if (!isVisible) return null;
                const disabled = profileLocked || !a.available || icebreakerLoading;
                const label = icebreakerLoading
                  ? "Generazione…"
                  : a.reason === "insufficient_credits"
                    ? `Servono ${cost} crediti`
                    : a.reason === "limit_reached"
                      ? "Limite raggiunto"
                      : icebreakerUsedThisSession > 0
                        ? `Genera nuova frase (${cost} crediti)`
                        : `Usa un Rompisilenzio (${cost} crediti)`;
                return (
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={handleIcebreaker}
                    className={`w-full mb-2 text-xs font-medium rounded-full px-3 py-1.5 transition-colors ${
                      disabled
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700"
                    }`}
                  >
                    {label}
                  </button>
                );
              })()
            )}
            {/* Richiedi foto — dal contesto della chat. Da profilo offline resta
                visibile ma spento, col motivo in un suggerimento a richiesta. */}
            {(() => {
              if (!showChatPhoto && !hasPrivatePhoto) return null;
              return (
                <>
                  {/* Foto privata: azione secondaria, a scomparsa. Il suo toggle
                      NON sta su nessuno dei due pulsanti (li accorcerebbe): e'
                      l'icona immagine nella riga di input, piu' in basso. Senza
                      il pulsante principale (feature foto in chat disattivata) la
                      privata resta sempre visibile: un toggle da solo non
                      avrebbe contesto. */}
                  {hasPrivatePhoto && (!showChatPhoto || showPrivatePhotoAction) && (() => {
                    const blocked =
                      profileLocked || privatePhotoLoading || recipientOffline;
                    const button = (
                      <button
                        type="button"
                        disabled={blocked}
                        onClick={handleRequestPrivatePhoto}
                        className={`w-full mb-2 text-xs font-medium rounded-full px-3 py-1.5 transition-colors ${
                          blocked
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-gradient-to-r from-rose-500 to-pink-600 text-white hover:from-rose-600 hover:to-pink-700"
                        }`}
                        data-testid="button-request-private-photo"
                      >
                        {privatePhotoLoading
                          ? "Foto in arrivo…"
                          : `🔒 Richiedi foto privata · ${privatePhotoCost?.cost ?? 10} crediti`}
                      </button>
                    );
                    // Solo da offline il pulsante ha bisogno di spiegarsi: gli
                    // altri motivi per cui e' spento li dice gia' la sua etichetta.
                    return recipientOffline ? (
                      <OfflinePhotoHint name={recipientUser.firstName}>
                        {button}
                      </OfflinePhotoHint>
                    ) : (
                      button
                    );
                  })()}

                  {showChatPhoto && (() => {
                    // Il bottone si spegne sia durante l'attesa sia quando manca
                    // il turno di conversazione: in entrambi i casi il server
                    // rifiuterebbe, meglio mostrarlo spento che far scoprire il
                    // limite con un errore dopo il click.
                    // `canRequest` viene dal server ed e' l'unica fonte di
                    // verita': il countdown locale mostra quanto manca, non
                    // decide. Se scendesse a 0 mentre manca ancora il turno, un
                    // bottone riattivato in anticipo si prenderebbe un 429.
                    const waiting = chatPhotoRetryMs > 0;
                    const blocked =
                      profileLocked ||
                      chatPhotoLoading ||
                      waiting ||
                      recipientOffline ||
                      chatPhotoAvailability.canRequest === false;
                    const mm = Math.floor(chatPhotoRetryMs / 60000);
                    const ss = Math.ceil((chatPhotoRetryMs % 60000) / 1000);
                    const button = (
                      <button
                        type="button"
                        disabled={blocked}
                        onClick={handleRequestChatPhoto}
                        className={`w-full mb-2 text-xs font-medium rounded-full px-3 py-1.5 transition-colors ${
                          blocked
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white hover:from-violet-600 hover:to-fuchsia-700"
                        }`}
                        data-testid="button-request-chat-photo"
                      >
                        {chatPhotoLoading
                          ? "Foto in arrivo…"
                          : waiting
                            ? `📸 Potrai richiederne un'altra tra ${mm > 0 ? `${mm}:${String(ss).padStart(2, "0")}` : `${ss}s`}`
                            // Da offline l'etichetta resta quella dell'azione:
                            // il motivo lo da' il suggerimento, non il pulsante.
                            : blocked && !recipientOffline
                              // Copre sia il turno mancante sia un cooldown
                              // residuo noto al server ma col countdown locale
                              // gia' a zero: spento con la label attiva
                              // sembrerebbe un bug.
                              ? "📸 Continua la chat per un'altra foto"
                              : `📸 Richiedi foto · ${chatPhotoAvailability.cost} crediti`}
                      </button>
                    );
                    return recipientOffline ? (
                      <OfflinePhotoHint name={recipientUser.firstName}>
                        {button}
                      </OfflinePhotoHint>
                    ) : (
                      button
                    );
                  })()}
                </>
              );
            })()}
            <form onSubmit={handleSendMessage} className="flex items-center gap-2">
              {/* Toggle della foto privata: sta qui, e non sui pulsanti sopra,
                  cosi' "Richiedi foto" resta intero e a piena larghezza. Precede
                  l'allegato: e' l'azione a valore, l'allegato e' di servizio.
                  Serve solo quando la privata e' effettivamente nascosta dietro
                  il pulsante principale. */}
              {hasPrivatePhoto && showChatPhoto && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-8 h-8 p-0 hover:bg-gray-100 rounded-full"
                  onClick={() => setShowPrivatePhotoAction((v) => !v)}
                  aria-expanded={showPrivatePhotoAction}
                  aria-label={
                    showPrivatePhotoAction
                      ? "Nascondi richiesta foto privata"
                      : "Mostra richiesta foto privata"
                  }
                  title={
                    showPrivatePhotoAction
                      ? "Nascondi foto privata"
                      : "Richiedi foto privata"
                  }
                  data-testid="button-toggle-private-photo"
                >
                  <Image
                    className={`w-4 h-4 text-white ${
                      showPrivatePhotoAction ? "" : "opacity-70"
                    }`}
                  />
                </Button>
              )}

              {chatImagesEnabled && (
              <div className="flex items-center gap-2">
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={handleImageSelect}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-8 h-8 p-0 hover:bg-gray-100 rounded-full"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={isImageSending}
                >
                  <Camera className="w-4 h-4 text-white" />
                </Button>
              </div>
              )}

              <div className="flex-1 relative">
                {isRecording ? (
                  <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-full">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                    </span>
                    <span className="text-sm text-red-600">
                      {Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')}
                    </span>
                    <button
                      type="button"
                      onClick={cancelRecording}
                      className="text-xs text-gray-500 hover:text-red-600 ml-2"
                    >
                      Annulla
                    </button>
                  </div>
                ) : (
                  <>
                    <textarea
                      ref={inputRef}
                      placeholder={
                        profileLocked
                          ? `Sblocca il profilo (${unlockCost} crediti) per scrivere`
                          : "Scrivi un messaggio..."
                      }
                      disabled={profileLocked}
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        e.target.style.height = "auto";
                        e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(e as any);
                        }
                      }}
                      rows={1}
                      className="w-full pr-10 pl-3 py-2 border border-gray-300 dark:border-gray-700 rounded-2xl bg-gray-50 focus:bg-white dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm leading-5 resize-none overflow-y-auto outline-none placeholder:truncate"
                      style={{ height: "36px", maxHeight: "120px" }}
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 p-0 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
                      onClick={() => setShowEmojiPicker((v) => !v)}
                      aria-label="Inserisci emoji"
                    >
                      <Smile className="w-4 h-4 text-white" />
                    </button>
                    {showEmojiPicker && (
                      <div ref={emojiPickerRef} className="absolute bottom-full right-0 mb-2 z-50">
                        <Picker
                          data={data}
                          onEmojiSelect={handleEmojiSelect}
                          theme="light"
                          locale="it"
                          previewPosition="none"
                          skinTonePosition="none"
                          perLine={7}
                          maxFrequentRows={2}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Mic button */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={`w-8 h-8 p-0 rounded-full transition-all ${isRecording
                  ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse'
                  : 'hover:bg-blue-100'
                  }`}
                disabled={isAudioSending}
                onMouseDown={async () => {
                  if (!isRecording) {
                    try {
                      await startRecording();
                    } catch (e) {
                      console.error('Failed to start recording:', e);
                    }
                  }
                }}
                onMouseUp={async () => {
                  if (isRecording && duration >= 1) {
                    setIsAudioSending(true);
                    setIsTyping(true);
                    try {
                      const blob = await stopRecording();
                      if (blob) {
                        // Create a local URL for immediate playback
                        const localAudioUrl = URL.createObjectURL(blob);
                        const tempMessageId = `temp-audio-${Date.now()}`;

                        // Add optimistic audio message immediately
                        queryClient.setQueryData(
                          ["/api/conversations", currentConversationId, "messages"],
                          (old: any[]) => {
                            const optimisticAudioMessage = {
                              id: tempMessageId,
                              content: localAudioUrl, // Use local blob URL for immediate playback
                              senderId: (user as any)?.id,
                              recipientId: recipientUser.id,
                              conversationId: currentConversationId,
                              createdAt: new Date().toISOString(),
                              isRead: false,
                              metadata: { duration },
                              _isOptimistic: true,
                            };
                            return [...(old || []), optimisticAudioMessage];
                          }
                        );

                        // Send audio to backend
                        const formData = new FormData();
                        formData.append('audio', blob, `audio_${Date.now()}.webm`);
                        formData.append('user_id', (user as any)?.id || 'unknown');
                        formData.append('session_id', currentConversationId || '');
                        formData.append('profile_id', recipientUser.id);
                        formData.append('ai_personality', '');
                        formData.append('duration', duration.toString());

                        const token = localStorage.getItem('auth_token');
                        const response = await fetch('/api/agent/audio-chat', {
                          method: 'POST',
                          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                          credentials: 'include',
                          body: formData,
                        });

                        if (response.ok) {
                          const data = await response.json();

                          // Revoke the local blob URL to free memory
                          URL.revokeObjectURL(localAudioUrl);
                          // Immediately update user credits from response
                          if (typeof data.currentCredits === 'number') {
                            queryClient.setQueryData(["/api/user"], (oldUser: any) => {
                              if (!oldUser) return oldUser;
                              return { ...oldUser, credits: data.currentCredits };
                            });
                          }
                          // Refresh messages to get the real S3 URL and agent response
                          queryClient.invalidateQueries({
                            queryKey: ["/api/conversations", currentConversationId, "messages"],
                          });
                          // Also invalidate user as fallback
                          queryClient.invalidateQueries({ queryKey: ["/api/user"] });
                        }
                      }
                    } catch (e) {
                      console.error('Failed to send audio:', e);
                    } finally {
                      setIsAudioSending(false);
                      setIsTyping(false);
                    }
                  } else if (isRecording) {
                    cancelRecording();
                  }
                }}
              >
                <Mic className="w-4 h-4 text-white" />
              </Button>

              <Button
                type="submit"
                variant="ghost"
                size="sm"
                className="w-8 h-8 p-0 hover:bg-blue-100 rounded-full"
                disabled={profileLocked || isRecording || !newMessage.trim()}
              >
                <Send className="w-4 h-4 text-white" />
              </Button>
            </form>
            </>
            )}
          </div>
        </>
      )
      }

      {/* Photo Lightbox — evita di aprire l'URL grezzo della foto in una nuova tab */}
      {photoLightbox && (
        <div
          className="fixed inset-0 z-[10001] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPhotoLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition-colors"
            onClick={(e) => { e.stopPropagation(); setPhotoLightbox(null); }}
            aria-label="Chiudi anteprima"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={photoLightbox}
            alt="Foto"
            className={`max-w-full max-h-[90vh] object-contain rounded-lg ${protectedImageClass}`}
            onClick={(e) => e.stopPropagation()}
            {...protectedImageProps}
          />
        </div>
      )}

      <OutOfCreditsModal
        open={showOutOfCredits}
        onOpenChange={(o) => {
          setShowOutOfCredits(o);
          // Alla chiusura torna il popup generico: lo shortfall vale solo per
          // l'azione che l'ha aperto.
          if (!o) setCreditShortfall(null);
        }}
        partnerName={recipientDetails?.firstName || recipientUserBase?.firstName}
        shortfall={creditShortfall}
      />
    </div >
  );

  if (!expanded) return windowEl;

  // Portal su <body>: fuori dal contenitore delle chat, cosi' il posizionamento
  // fixed funziona davvero e la finestra non resta anche nell'angolo.
  return createPortal(
    <>
      <div
        aria-hidden="true"
        onClick={() => setIsFullscreen(false)}
        className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm"
      />
      {windowEl}
    </>,
    document.body,
  );
}
